/**
 * Telegram handle parsing and intro-message helpers.
 *
 * `contact_for_matches` is a free-form text field — at registration the
 * placeholder hints "@alex_ivanov on Telegram, WhatsApp +351..." and we
 * accept anything. After a mutual match the UI tries to detect a Telegram
 * handle inside that string and render a one-tap "Message in Telegram"
 * button. Anything we cannot parse stays as plain selectable text.
 */

/** Canonical Telegram username — 5–32 chars, [A-Za-z0-9_], must contain
 *  at least one letter. Numeric-only handles do exist in Telegram but
 *  they collide with phone numbers, so we exclude them. */
const TELEGRAM_HANDLE = /(?:^|[\s@/])([A-Za-z][A-Za-z0-9_]{4,31}|[A-Za-z0-9_]*[A-Za-z][A-Za-z0-9_]*)/;

/**
 * Extract a Telegram handle from a free-form contact string.
 *
 * Recognised forms:
 *   "@alex_ivanov"
 *   "alex_ivanov"
 *   "t.me/alex_ivanov"
 *   "https://t.me/alex_ivanov"
 *   "Telegram: @alex_ivanov, WhatsApp +351..."
 *
 * Returns the bare handle (no @, no URL) or null if nothing looks like
 * a Telegram username.
 */
export function parseTelegramHandle(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // 1. Full t.me URL (with or without protocol).
  const urlMatch = trimmed.match(/t\.me\/([A-Za-z0-9_]{5,32})/i);
  if (urlMatch) return urlMatch[1];

  // 2. @handle anywhere in the string.
  const atMatch = trimmed.match(/@([A-Za-z0-9_]{5,32})/);
  if (atMatch) return atMatch[1];

  // 3. Bare handle as the entire field. We require it to start with a
  //    letter to avoid grabbing phone numbers, and to be at least 5
  //    chars long per Telegram's rules.
  if (/^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(trimmed)) return trimmed;

  // 4. Last resort — first handle-shaped token in the string. Used when
  //    users write "Tg alex_ivanov, WA +351 ...". Phone-style numeric
  //    chunks won't match because we require a letter.
  const fallback = trimmed.match(TELEGRAM_HANDLE);
  if (fallback && /[A-Za-z]/.test(fallback[1]) && fallback[1].length >= 5) {
    return fallback[1];
  }

  return null;
}

/** Open the user's Telegram app/web client with the given handle. */
export function telegramDeepLink(handle: string): string {
  return `https://t.me/${encodeURIComponent(handle)}`;
}

/**
 * Build the canonical intro message that the matched participant copies
 * into Telegram before sending. Kept short and human — meetup organisers
 * have repeatedly asked for "not corporate".
 */
export function buildIntroMessage(args: {
  myName: string | null | undefined;
  otherName: string | null | undefined;
  eventTitle: string | null | undefined;
  profileUrl: string;
}): string {
  // Defensive: every field comes from RPC / localStorage and could be missing
  // in legacy data. We render plausible defaults rather than throw.
  const myName = (args.myName ?? "").trim() || "a fellow attendee";
  const otherName = (args.otherName ?? "").trim() || "there";
  const eventTitle = (args.eventTitle ?? "").trim() || "our event";
  const firstName = myName.split(/\s+/)[0] ?? myName;
  return [
    `Hi ${otherName}! It's ${firstName} — we matched on Finally at "${eventTitle}".`,
    `Quick intro: ${args.profileUrl}`,
    `Up for a 15-min chat this week?`,
  ].join("\n\n");
}
